import CoreAPIService from '../../../utils/core-api';
import i18n from '../../../utils/i18n';
import ObjectUtils from '../../../utils/objectUtils';
import UserDataUtils, { Favorite } from '../../../utils/userDataUtils';
import UserUtils from '../../../utils/userUtils';
import { fetchAction } from '../../actions';

export const name: string = 'home';

export const MENUS_KEY: string = 'menus';

export const types = {
    USERSTORAGE_GET: name + '/USERSTORAGE_GET',
    USERSTORAGE_GET_ALL: name + '/USERSTORAGE_GET_ALL',
    USERSTORAGE_SET: name + '/USERSTORAGE_SET',
    USERSTORAGE_REMOVE: name + '/USERSTORAGE_REMOVE',
    USERSTORAGE_CLEAR: name + '/USERSTORAGE_CLEAR',
    REDIRECT_HOME: name + '/REDIRECT_HOME',
    CLEAR_REDIRECT: name + '/CLEAR_REDIRECT',
};

export interface UserStorage {
    /** Absent when never fetched OR when the user has nothing stored: see `loadedKeys`. */
    menus?: { favorites?: Favorite[] };
}

interface HomeState {
    userStorage: UserStorage;
    /**
     * Keys whose value has actually been read from the server.
     * A key missing from `userStorage` is ambiguous on its own: it can mean
     * "the user has nothing stored" OR "we never fetched it". Writing a key
     * without knowing which is a full overwrite of the server value, so every
     * write must check this first.
     */
    loadedKeys: string[];
    /** True once a GET /userstorage (all keys) succeeded: any missing key is then genuinely empty. */
    allLoaded: boolean;
    /**
     * Id of the user this cache belongs to. Logging out does not reset the store and logging in
     * is a client-side redirect, so without this a second user connecting in the same tab would
     * inherit — and then overwrite their own favorites with — the previous user's list.
     */
    userId: number | string | null;
    redirect: {};
}

const initialState: HomeState = {
    userStorage: {} as UserStorage,
    loadedKeys: [],
    allLoaded: false,
    userId: null,
    redirect: {},
};

// GET /userstorage : retourne toutes les data
// GET /userstorage/key : retourne juste la clé key
// GET /userstorage {"keys": ["aaa", "bbb"]} : retourne les clés "aaa" et "bbb"
// PUT /userstorage {"keys": {"favorites":"aaa"}} : ajoute la clé "favorites" avec la valeur "aaa" (bien sûr tu peux ajouter d’autres clés)
// PUT /userstorage/favorites {"value": "aaa"}  : pareil, mais c’est une autre syntaxe (et limité à une clé du coup)
// DELETE /userstorage/favorites : supprime la clé favorites
// DELETE /userstorage {"keys": ["favorites"]}  : idem, mais tu peux supprimer plusieurs clés
// DELETE /userstorage {"keys": "all"} : supprime TOUT !

const reducer = (state = initialState, action): HomeState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const key = parameters ? parameters.key : 0;
    switch (action.type) {
        case types.USERSTORAGE_GET_ALL:
            return {
                ...state,
                userStorage: payload,
                loadedKeys: [],
                allLoaded: true,
                userId: currentUserId(),
            };
        case types.USERSTORAGE_GET:
        case types.USERSTORAGE_SET:
            return {
                ...state,
                userStorage: {
                    ...state.userStorage,
                    [key]: payload[key],
                },
                loadedKeys: addLoadedKey(state.loadedKeys, key),
                userId: currentUserId(),
            };
        case types.USERSTORAGE_REMOVE:
            return {
                ...state,
                userStorage: ObjectUtils.removePathFromObject(state.userStorage, [key]),
                // Still loaded, just known-empty now.
                loadedKeys: addLoadedKey(state.loadedKeys, key),
                userId: currentUserId(),
            };
        case types.USERSTORAGE_CLEAR:
            return {
                ...state,
                userStorage: {},
                loadedKeys: [],
                allLoaded: true,
                userId: currentUserId(),
            };
        case types.REDIRECT_HOME:
            payload.error.message = i18n.t('common:access_denied');
            return {
                ...state,
                redirect: payload,
            };

        case types.CLEAR_REDIRECT:
            return {
                ...state,
                redirect: {},
            };
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    getUserStorage: (key) =>
        fetchAction(CoreAPIService.get(`/core/userstorage/${key}`), types.USERSTORAGE_GET, { key }),
    getAllUserStorage: () => fetchAction(CoreAPIService.get(`/core/userstorage`), types.USERSTORAGE_GET_ALL, {}),
    setUserStorage: (key, value) =>
        fetchAction(CoreAPIService.put(`/core/userstorage/${key}`, { value }), types.USERSTORAGE_SET, { key }),
    removeUserStorage: (key) =>
        fetchAction(CoreAPIService.delete(`/core/userstorage/${key}`), types.USERSTORAGE_REMOVE, { key }),
    clearUserStorage: () =>
        fetchAction(CoreAPIService.delete(`/core/userstorage`, { keys: 'all' }), types.USERSTORAGE_CLEAR, {}),
    clearError: () => ({ type: types.CLEAR_REDIRECT, payload: {} }),

    /**
     * Loads the `menus` key once per session. Safe to call from any mount.
     */
    loadMenus: () => (dispatch, getState) => {
        if (selectors.isUserStorageLoaded(getState(), MENUS_KEY)) {
            return Promise.resolve();
        }
        return dispatch(actions.getUserStorage(MENUS_KEY));
    },

    /**
     * Adds or removes `path` from the favorites, then writes the whole `menus` key back.
     *
     * Because the write replaces the server value entirely, we must never build the new
     * list from a `menus` we haven't read: doing so silently wipes every existing favorite.
     * So we load it first when needed, and bail out if that load fails.
     */
    toggleFavorite:
        (path: string, name: string = '', removeIfExist: boolean = true) =>
        async (dispatch, getState) => {
            if (!selectors.isUserStorageLoaded(getState(), MENUS_KEY)) {
                await dispatch(actions.getUserStorage(MENUS_KEY));
                if (!selectors.isUserStorageLoaded(getState(), MENUS_KEY)) {
                    // The GET failed: an empty list here would mean losing everything.
                    return;
                }
            }

            const favorites = selectors.favoritesList(getState());
            const next = UserDataUtils.computeFavorites(favorites, path, name, removeIfExist);
            if (next === null) {
                return;
            }
            return dispatch(actions.setUserStorage(MENUS_KEY, { favorites: next }));
        },
};

const localState = (state: any): HomeState => state[name] || initialState;
export const selectors = {
    /** Empty as soon as the cache belongs to another user: never expose or write someone else's data. */
    userStorage: (state) => (isCurrentUserStorage(state) ? localState(state).userStorage || {} : {}),
    /** The favorites as stored, or [] — meaningless unless `menusLoaded` is true. */
    favoritesList: (state): Favorite[] => selectors.userStorage(state)?.menus?.favorites || [],
    /** A `{ [path]: true }` map of the favorites, or null while `menus` is not loaded yet. */
    favorites: (state) => (selectors.menusLoaded(state) ? getFavorites(selectors.favoritesList(state)) : null),
    menusLoaded: (state): boolean => selectors.isUserStorageLoaded(state, MENUS_KEY),
    isUserStorageLoaded: (state, key: string): boolean => {
        if (!isCurrentUserStorage(state)) {
            return false;
        }
        const { allLoaded, loadedKeys = [] } = localState(state);
        return allLoaded || loadedKeys.includes(key);
    },
    redirect: (state) => localState(state).redirect,
};

// helpers
const currentUserId = (): number | string | null => UserUtils.getUser()?.user?.id ?? null;

const isCurrentUserStorage = (state): boolean => localState(state).userId === currentUserId();

const addLoadedKey = (loadedKeys: string[], key: string): string[] =>
    loadedKeys.includes(key) ? loadedKeys : [...loadedKeys, key];

const getFavorites = (favorites: Favorite[]) => {
    const res = {};
    for (const favorite of favorites) {
        res[favorite.to] = true;
    }
    return res;
};
